import RenderEntity, { RENDER_ENTITY_FORMAT3 } from 'ui-component/renders/RenderEntity';
// material-ui
import { Stack, Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';
import capabilities from './capabilities';
import { TASK_TREE_MAX_DEPTH } from './tree';

// assets
import AssignmentTwoToneIcon from '@mui/icons-material/AssignmentTwoTone';
import AddCircleTwoToneIcon from '@mui/icons-material/AddCircleTwoTone';

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canAdd':
                            if (
                                params?.row?.deletedAt ||
                                params?.row?.treeDepth >= TASK_TREE_MAX_DEPTH - 1 ||
                                typeof onClickActions?.onClickRowActionAddSubTask !== 'function'
                            ) {
                                return;
                            }

                            gridActions.push({
                                key: `actionaddsubtask-row${params?.row?.id}`,
                                icon: <AddCircleTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-add',
                                position: 0,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionAddSubTask' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionAddSubTask === 'function'
                                    ) {
                                        onClickActions.onClickRowActionAddSubTask(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={params.value}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <AssignmentTwoToneIcon color="primary" sx={{ fontSize: '0.875rem' }} />
                            <Typography
                                variant="inherit"
                                color="inherit"
                                sx={{
                                    overflow: 'hidden',
                                    display: 'block',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                    // ':hover': { color: 'primary.main' }
                                    // cursor: 'pointer'
                                }}
                            >
                                {params.value}
                            </Typography>
                        </Stack>
                    </Tooltip>
                ),
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'requiresExpectedValueOnCompletion',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'entity-tplmgmt-task-requiresexpectedvalueoncompletion'
        },
        {
            field: 'requiresContactsOnCreation',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'entity-tplmgmt-task-requirescontactsoncreation'
        },
        {
            field: 'businessOpportunity',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="tplBusinessOpportunity"
                    renderPattern="<name>"
                    renderTooltipPattern="<name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-tplmgmt-businessopportunity'
        },
        {
            field: 'workOrder',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="tplWorkOrder"
                    renderPattern="<name>"
                    renderTooltipPattern="<name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-tplmgmt-workorder'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                name: true,
                description: true,
                requiresExpectedValueOnCompletion: false,
                requiresContactsOnCreation: false,
                businessOpportunity: false,
                workOrder: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'name',
                'description',
                'requiresExpectedValueOnCompletion',
                'requiresContactsOnCreation',
                'businessOpportunity',
                'workOrder',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                name: { width: 150 },
                description: { width: 100 },
                requiresExpectedValueOnCompletion: { width: 160 },
                requiresContactsOnCreation: { width: 160 },
                businessOpportunity: { width: 120 },
                workOrder: { width: 120 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'asc' }]
        }
    }
};

export default initialState;
